import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { SelectButton } from 'primereact/selectbutton';
import { Toast } from 'primereact/toast';
import { translatedMessage } from '../../service/LanguageService';
import { ApplicationService } from '../../service/ApplicationService';
import { EvaluationService } from '../../service/EvaluationService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ApplicationEvaluationDialog from '../../components/evaluation/ApplicationEvaluationDialog';
import StorageFolderFileList from '../../components/file/StorageFolderFileList';
import ApplicationResultDialog from '../../components/application/ApplicationResultDialog';
import useAuth from '../../hooks/useAuth';
import EnumService from '../../service/EnumService';
import ApplicationEducation from '../competition/application/ApplicationEducation';
import ApplicationWorkExperience from '../competition/application/ApplicationWorkExperience';
import ApplicationITKnowledge from '../competition/application/ApplicationITKnowledge';
import ApplicationLanguage from '../competition/application/ApplicationLanguage';
import ErrorMessageDisplayComponent from '../../components/ErrorMessageDisplayComponent';
import EvaluationFormPage from './EvaluationFormPage';
import UserApplicationList from '../../components/application/UserApplicationList';
import UserApplicationPublicServant from '../../components/application/UserApplicationPublicServant';
import CompetitionUtils from '../../utilities/CompetitionUtils';

const allSections = [
    { label: translatedMessage('evaluation.evaluation'), code: 'GENERAL_EVALUATION', icon: 'fa-solid fa-user' },
    { label: translatedMessage('profile.education'), code: 'EDUCATION', icon: 'fa-solid fa-user-graduate' },
    { label: translatedMessage('profile.workExperience'), code: 'EXPERIENCE', icon: 'fa-solid fa-building-columns' },
    { label: translatedMessage('profile.itKnowledge'), code: 'IT', icon: 'fa-solid fa-computer' },
    { label: translatedMessage('profile.language'), code: 'LANGUAGE', icon: 'fa-solid fa-globe' },
    { label: translatedMessage('application.form'), code: 'FORM_EVALUATION', icon: 'fa-regular fa-file-lines' },
    { label: translatedMessage('application.files'), code: 'FILES', icon: 'fa-regular fa-file' },
    { label: translatedMessage('competition.evaluation.tab.title'), code: 'COMP', icon: 'fa-solid fa-list-ol' },
    { label: translatedMessage('generic.backToList'), code: 'BACK', icon: 'fa-solid fa-arrow-left' }
];

let sections = [...allSections];

const EvaluationPage = () => {
    const [committee, setCommittee] = useState({});
    const [applicationFormFields, setApplicationFormFields] = useState([]);
    const [application, setApplication] = useState(null);
    const [evaluation, setEvaluation] = useState({});
    const [result, setResult] = useState({});

    const [applicationEvaluationDialogVisible, setApplicationEvaluationDialogVisible] = useState(false);
    const [editApplicationResultVisible, setEditApplicationResultVisible] = useState(false);

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("generic.error.get.data");
    const [section, setSection] = useState(sections[0]);
    const [fieldEvaluationStatuses, setFieldEvaluationStatuses] = useState([]);
    const [evaluationStatuses, setEvaluationStatuses] = useState([]);
    const [positionName, setPositionName] = useState(null);

    const toast = useRef(null);

    const applicationService = useMemo(() => new ApplicationService(), []);
    const evaluationService = useMemo(() => new EvaluationService(), []);

    const { competitionIdParam, evaluationIdParam } = useParams();

    const [searchParams] = useSearchParams();

    const { auth } = useAuth();

    const navigate = useNavigate();

    useEffect(() => {
        setIsLoading(true)

        if (searchParams.get('section')) {
            setSection(sections.find((section) => section.code === searchParams.get('section')));
        }

        const filterSections = (committee, _application) => {
            if (committee.type !== 'COMPETITION' && committee.type !== 'ELIGIBILITY_CONTESTATION') {
                sections = allSections.filter((_section) => _section.code !== 'CLARIFICATION' && _section.code !== 'FORM_EVALUATION' && _section.code !== 'FILES');
            }

            if(_application && CompetitionUtils.isNationalCompetition(_application.competition.type)) {
                sections = sections.filter((_section) => _section.code !== 'COMP');
            }
        };

        const populateFieldEvaluationStatuses = async () => {
            await EnumService.getEnumByName('FieldEvaluationStatus').then((statuses) => setFieldEvaluationStatuses(statuses));
        };

        const populateEvaluationStatuses = async () => {
            await EnumService.getEnumByName('EvaluationStatus').then((statuses) => setEvaluationStatuses(statuses));
        };

        const getEvaluation = async () => {
            await evaluationService.getEvaluationById(evaluationIdParam)
                .then(async (_evaluation) => {
                    if (_evaluation?.evaluationCommitteeUser?.user.id === auth.user.id) {
                        _evaluation.fieldsEvaluation = await evaluationService.getApplicationFieldEvaluationsList(_evaluation.id)
                        setEvaluation(_evaluation)
                        setApplication(_evaluation.application)
                        setPositionName(_evaluation.application?.positionName)

                        getFormFields(_evaluation?.application?.id)

                        populateEvaluationStatuses();
                        populateFieldEvaluationStatuses();
                        setCommittee(_evaluation.evaluationCommitteeUser.evaluationCommittee);
                        filterSections(_evaluation.evaluationCommitteeUser.evaluationCommittee, _evaluation.application);

                        if (!_evaluation.evaluationCommitteeUser.evaluationCommittee.active) {
                            getApplicationResult(_evaluation?.evaluationCommitteeUser.evaluationCommittee.id, _evaluation?.application?.id);
                        }
                    } else {
                        setError(true)
                        setErrorMessage("generic.access-denied")
                    }

                    setIsLoading(false)
                })
                .catch((error) => {
                    setError(true)
                    setErrorMessage(error)
                    setIsLoading(false)
                    toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 })
                });
        };

        const getFormFields = async (applicationId) => {
            applicationService.getApplicationFormFieldList(applicationId)
                .then((_fields) => {
                    setApplicationFormFields(_fields);
                })
                .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
        };

        const getApplicationResult = (committeeId, applicationId) => {
            evaluationService.getCommitteeApplicationResult(committeeId, applicationId)
                .then((_result) => {
                    setResult(_result);
                })
                .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
        };

        if (section.code === 'GENERAL_EVALUATION') {
            getEvaluation()
        } else {
            setIsLoading(false)
        }
    }, [applicationService, evaluationService, evaluationIdParam, searchParams, section, auth.user.id]);

    const navigationTemplate = (option) => {
        return (
            <>
                <FontAwesomeIcon icon={option.icon} className="mr-1 " />
                <span>{option.label}</span>
            </>
        );
    };

    const handleSectionChange = (value) => {
        if (value) {
            if (value.code === 'BACK') {
                navigate(`/competition-evaluation/${competitionIdParam}`);
            } else {
                setSection(value);
            }
        }
    };

    const openApplicationResultDialog = () => {
        setEditApplicationResultVisible(true);
    };

    const afterUpdateApplicationStatus = (savedApplication) => {
        setApplication(savedApplication);
    };

    const onEvaluationChange = async (evaluationId, comment, status, updatedEvaluation) => {
        updatedEvaluation.fieldsEvaluation = await evaluationService.getApplicationFieldEvaluationsList(updatedEvaluation.id)
        setEvaluation(updatedEvaluation)
        setApplication(updatedEvaluation.application)
    };

    if (isLoading) {
        return (
            <div className="w-full flex align-items-center">
                <Toast ref={toast} />
                <ProgressSpinner />
            </div>
        );
    } else if (error) {
        return (
            <div className='w-full flex align-items-center'>
                <ErrorMessageDisplayComponent message={errorMessage} />
            </div>
        )
    } else {
        return (
            <>
                <Toast ref={toast} />

                <div className="grid h-full">
                    <div className="col-12">
                        <div className="card h-full">
                            <h3 className="mb-1">
                                {translatedMessage('evaluation.evaluation')} {translatedMessage('generic.for')} {application?.firstName} {application?.lastName}
                            </h3>
                            <div className="mb-5">
                                <SelectButton value={section} options={sections} onChange={(e) => handleSectionChange(e.value)} itemTemplate={navigationTemplate} className="pcn-selectbutton-slim" />
                            </div>

                            {section.code === 'GENERAL_EVALUATION' && (
                                <div className="w-full grid pcn-data-view-panel">
                                    <div className="col-12 md:col-6">
                                        <div className="filed-label">{translatedMessage('competition.competition')}</div>
                                        <div className="filed-value">{application?.competition?.name}</div>
                                    </div>
                                    {!CompetitionUtils.isJobCompetition(application?.competition?.type) && positionName &&
                                        <div className="col-12 md:col-6">
                                            <div className="filed-label">{translatedMessage('application.publicPosition')}</div>
                                            <div className="filed-value">
                                                {positionName}
                                            </div>
                                        </div>
                                    } 
                                    <div className="col-12 md:col-6">
                                        <div className="filed-label">{translatedMessage('evaluation.committee')}</div>
                                        <div className="filed-value">{committee.name}</div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="filed-label">{translatedMessage('generic.type')}</div>
                                        <div className="filed-value">{translatedMessage((CompetitionUtils.isJobCompetition(application?.competition?.type) ? "job." : "") +
                                            'EvaluationCommitteeType.' + committee.type)}</div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="filed-label">{translatedMessage('committee.member.role')}</div>
                                        <div className="filed-value">
                                            {evaluation.evaluationCommitteeUser.isPresident
                                                ? translatedMessage('committee.member.isPresident')
                                                : evaluation.evaluationCommitteeUser.isSecretary
                                                    ? translatedMessage('committee.member.isSecretary')
                                                    : translatedMessage('committee.member.isSimpleMember')
                                            }
                                        </div>
                                    </div>
                                    <div className="col-12 mt-3 mb-3">
                                        <h5 className='mb-1'>{translatedMessage("evaluation.my")}</h5>
                                        <div className="w-full grid pcn-data-view-panel">
                                            <div className='col-12 pb-0 mb-2'>
                                                <div className="filed-label">{translatedMessage('generic.status')}</div>
                                                <span className={`status status-evaluation-${evaluation?.status.toString().toLowerCase()}`} >
                                                    {translatedMessage('EvaluationStatus.' + evaluation?.status)}
                                                </span>
                                            </div>
                                            <div className='col-12 pb-0'>
                                                <div className='flex align-items-center'>
                                                    <div className="filed-label mb-0">{translatedMessage('evaluation.comment')}</div>
                                                    {committee.active &&
                                                        <Button label={translatedMessage('generic.edit')} icon="pi pi-pencil" className="p-button-text pcn-button-slim ml-2"
                                                            onClick={() => setApplicationEvaluationDialogVisible(true)} />
                                                    }
                                                </div>
                                                <div className='filed-value text-justify'>{evaluation?.comment || '-'}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <h5 className='mb-1'>{translatedMessage(committee.active ? 'evaluation.evaluationStatus' : 'evaluation.result')}</h5>
                                        <div className="filed-value flex align-items-center">
                                            {committee.active ? (
                                                <span className={`status status-application-${application.status.toString().toLowerCase()}`}>{translatedMessage('ApplicationStatus.' + application.status)}</span>
                                            ) : (
                                                <span className={`status status-result-${result?.result?.toString().toLowerCase()}`}>{translatedMessage('Result.' + result?.result)}</span>
                                            )}
                                            {committee.active && evaluation?.evaluationCommitteeUser.isSecretary && (
                                                <div className='ml-2'>
                                                    <Button label={translatedMessage('application.result.set')} icon="pi pi-pencil"
                                                        className="p-button-text pcn-button-slim" onClick={openApplicationResultDialog} />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {section.code === 'EDUCATION' &&
                                <ApplicationEducation
                                    application={application}
                                    formFields={applicationFormFields}
                                    evaluation={evaluation}
                                    statusOptions={fieldEvaluationStatuses}
                                    isForEvaluation={true}
                                    hideFillInExpanation={true}
                                    isCommitteeActive={committee.active}
                                    viewOnly={false}
                                />
                            }

                            {section.code === 'EXPERIENCE' &&
                                <ApplicationWorkExperience
                                    application={application}
                                    formFields={applicationFormFields}
                                    evaluation={evaluation}
                                    statusOptions={fieldEvaluationStatuses}
                                    isForEvaluation={true}
                                    hideFillInExpanation={true}
                                    isCommitteeActive={committee.active}
                                    viewOnly={false}
                                />
                            }

                            {section.code === 'IT' &&
                                <ApplicationITKnowledge
                                    application={application}
                                    formFields={applicationFormFields}
                                    evaluation={evaluation}
                                    statusOptions={fieldEvaluationStatuses}
                                    isForEvaluation={true}
                                    isCommitteeActive={committee.active}
                                    viewOnly={false}
                                />
                            }

                            {section.code === 'LANGUAGE' &&
                                <ApplicationLanguage
                                    application={application}
                                    formFields={applicationFormFields}
                                    evaluation={evaluation}
                                    statusOptions={fieldEvaluationStatuses}
                                    isForEvaluation={true}
                                    hideFillInExpanation={true}
                                    isCommitteeActive={committee.active}
                                    viewOnly={false}
                                />
                            }

                            {section.code === 'FORM_EVALUATION' && (
                                <EvaluationFormPage
                                    formFields={applicationFormFields}
                                    evaluation={evaluation}
                                    statusOptions={fieldEvaluationStatuses}
                                    isCommitteeActive={committee.active}
                                    viewOnly={false}
                                />
                            )}

                            {section.code === 'FILES' &&
                                <StorageFolderFileList
                                    applicationId={application?.id}
                                    folderId={application.folder.id}
                                    folderName={application.competition.name}
                                    showUploadButton={false}
                                    pageTitle={translatedMessage('application.files')}
                                    readOnly={true}
                                    evaluationApplicationDocs={true}
                                    hideFileDate={true}
                                />
                            }

                            {CompetitionUtils.isJobCompetition(application?.competition.type) && section.code === 'COMP' && <>
                                <UserApplicationList application={application} />
                                <UserApplicationPublicServant application={application} afterCheck={(a) => setApplication(a)}/>
                            </>}
                        </div>
                    </div>
                </div>

                <ApplicationResultDialog
                    visible={editApplicationResultVisible}
                    visibleSetter={setEditApplicationResultVisible}
                    application={application}
                    afterSave={afterUpdateApplicationStatus}
                />

                <ApplicationEvaluationDialog
                    visible={applicationEvaluationDialogVisible}
                    visibleSetter={setApplicationEvaluationDialogVisible}
                    applicationEvaluation={evaluation}
                    afterSave={onEvaluationChange}
                    statusOptions={evaluationStatuses}
                    readOnly={false} />
            </>
        );
    }
};

export default EvaluationPage;
